/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useState, useMemo } from 'react';
import { Tag, Typography, Card, Spin, Select } from 'antd';
import {
  PhoneOutlined,
  VideoCameraOutlined,
  DeploymentUnitOutlined,
  LineOutlined,
} from '@ant-design/icons';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import AppointmentTable from './AppointmentTable';
import { usePatientStore } from '../../store';
import { parseFieldSetup, translateStatus } from '../../utils/stringUtils';

dayjs.extend(utc);
const { Text } = Typography;

const PatientTable = ({ userInfo, messageApi }) => {
  const {
    activeCalls,
    fetchActiveCalls,
    isFetchingPatients,
    patientMRAppointments,
  } = usePatientStore((state) => ({
    activeCalls: state.activeCalls,
    fetchActiveCalls: state.fetchActiveCalls,
    isFetchingPatients: state.isFetchingPatients,
    patientMRAppointments: state.patientMRAppointments,
  }));

  console.log('activeCalls', activeCalls);

  const [expandedRows, setExpandedRows] = useState([]);
  console.log('expandedRows', expandedRows);
  const [filterCallStage, setFilterCallStage] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);

  // Check for matches between Active Calls and MR Appointments
  useEffect(() => {
    if (!activeCalls?.length || !patientMRAppointments?.length) {
      return;
    }

    const matches = [];

    activeCalls.forEach((activeCall) => {
      // Get Active Call patient info
      const activePatientFirstName = parseFieldSetup(
        activeCall.patientFirstName || ''
      )
        .toLowerCase()
        .trim();
      const activePatientLastName = parseFieldSetup(
        activeCall.patientLastName || ''
      )
        .toLowerCase()
        .trim();
      const activeDOB = activeCall.dob
        ? dayjs(activeCall.dob).format('YYYY-MM-DD')
        : '';

      // Skip if Active Call doesn't have complete patient info
      if (!activePatientFirstName || !activePatientLastName || !activeDOB) {
        return;
      }

      // Skip invalid calls with patient names starting with "2025-"
      if (
        activePatientFirstName.startsWith('2025-') ||
        activePatientLastName.startsWith('2025-')
      ) {
        return;
      }

      patientMRAppointments.forEach((appointment) => {
        // Get MR Appointment patient info
        const apptPatientFirstName = parseFieldSetup(
          appointment.patientfirstName || ''
        )
          .toLowerCase()
          .trim();
        const apptPatientLastName = parseFieldSetup(
          appointment.patientlastName || ''
        )
          .toLowerCase()
          .trim();
        const apptDOB = appointment.patientdob
          ? dayjs(appointment.patientdob).format('YYYY-MM-DD')
          : '';

        // Skip if MR Appointment doesn't have complete patient info
        if (!apptPatientFirstName || !apptPatientLastName || !apptDOB) {
          return;
        }

        // Check if both patient name and DOB match
        if (
          activePatientFirstName === apptPatientFirstName &&
          activePatientLastName === apptPatientLastName &&
          activeDOB === apptDOB
        ) {
          matches.push({
            activeCall: {
              // Patient info
              firstName: activeCall.patientFirstName,
              lastName: activeCall.patientLastName,
              dob: activeDOB,
              // Call details - preserve original data
              caseID: activeCall.caseID,
              taskSid: activeCall.taskSid,
              agentName: activeCall.agentName,
              startTime: activeCall.startTime,
              callStep: activeCall.callStep,
              currentStatus: activeCall.currentStatus,
              callType: activeCall.callType,
              urgency: activeCall.urgency,
              outcome: activeCall.outcome,
              isMultiPatient: activeCall.isMultiPatient,
              // Preserve all other fields from original activeCall
              ...activeCall,
            },
            appointment: {
              firstName: appointment.patientfirstName,
              lastName: appointment.patientlastName,
              dob: apptDOB,
              appointmentguid: appointment.appointmentguid,
              status: appointment.status,
            },
          });
        }
      });
    });

    console.log(`🔍 Active Call & MR Appointment Matches: ${matches.length}`);
    if (matches.length > 0) {
      console.log('📋 Matched Records:', matches);
    }

    // Save matched pairs to state
    setMatchedPairs(matches);
  }, [activeCalls, patientMRAppointments]);

  // Filter out matched Active Calls (remove them from left side)
  const filteredActiveCalls = useMemo(() => {
    if (!activeCalls?.length) {
      return [];
    }

    let filtered = activeCalls.filter((call) => {
      // Filter out invalid calls with patient names starting with "2025-"
      const firstName = parseFieldSetup(call.patientFirstName || '').trim();
      const lastName = parseFieldSetup(call.patientLastName || '').trim();

      if (firstName.startsWith('2025-') || lastName.startsWith('2025-')) {
        return false;
      }

      return true;
    });

    // Remove matched calls
    if (matchedPairs?.length) {
      const matchedActiveCallIds = new Set(
        matchedPairs.map(
          (pair) => `${pair.activeCall.caseID}_${pair.activeCall.taskSid}`
        )
      );

      filtered = filtered.filter(
        (call) => !matchedActiveCallIds.has(`${call.caseID}_${call.taskSid}`)
      );
    }

    return filtered;
  }, [activeCalls, matchedPairs]);

  const handleExpand = (record) => {
    if (record?.isMultiPatient === true) {
      console.log('record', record);
      if (
        expandedRows.includes(
          `${record?.caseID ?? ''} ${record?.taskSid ?? ''} ${record?.patientFirstName ?? ''} ${record?.patientLastName ?? ''}`
        )
      ) {
        setExpandedRows(
          expandedRows.filter(
            (item) =>
              item !==
              `${record?.caseID ?? ''} ${record?.taskSid ?? ''} ${record?.patientFirstName ?? ''} ${record?.patientLastName ?? ''}`
          )
        );
      } else {
        setExpandedRows([
          ...expandedRows,
          `${record?.caseID ?? ''} ${record?.taskSid ?? ''} ${record?.patientFirstName ?? ''} ${record?.patientLastName ?? ''}`,
        ]);
      }
    }
  };

  useEffect(() => {
    fetchActiveCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Main Content Row */}
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 2 }}>
          {isFetchingPatients ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginLeft: '16px',
                  marginBottom: '12px',
                  alignItems: 'center',
                }}
              >
                <Select
                  placeholder="Filter by Call Stage"
                  allowClear
                  style={{ width: 200 }}
                  onChange={(value) => setFilterCallStage(value)}
                  options={[
                    ...new Set(
                      activeCalls
                        .map((call) => call?.callStep)
                        .filter((stage) => stage)
                    ),
                  ].map((stage) => ({ label: stage, value: stage }))}
                />
                <Select
                  placeholder="Filter by Status"
                  allowClear
                  style={{ width: 200 }}
                  onChange={(value) => setFilterStatus(value)}
                  options={[
                    ...new Set(
                      activeCalls
                        .map((call) =>
                          translateStatus(call?.currentStatus ?? '')
                        )
                        .filter((status) => status)
                    ),
                  ].map((status) => ({ label: status, value: status }))}
                />
              </div>
              <div
                style={{
                  height: 'calc(125vh - 500px)',
                  overflowY: 'auto',
                  paddingRight: '8px',
                  marginLeft: '16px',
                  marginRight: '8px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {filteredActiveCalls && filteredActiveCalls.length > 0 ? (
                    filteredActiveCalls
                      .filter((record) => {
                        if (
                          filterCallStage &&
                          record?.callStep !== filterCallStage
                        )
                          return false;
                        if (
                          filterStatus &&
                          translateStatus(record?.currentStatus ?? '') !==
                            filterStatus
                        )
                          return false;
                        return true;
                      })
                      .map((record) => (
                        <Card
                          key={`${record?.caseID ?? ''} ${record?.taskSid ?? ''} ${record?.patientFirstName ?? ''} ${record?.patientLastName ?? ''}`}
                          size="small"
                          className="active-call-card"
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #d0d0d0',
                            cursor:
                              record?.isMultiPatient === true
                                ? 'pointer'
                                : 'default',
                            transition: 'all 0.3s ease',
                          }}
                          onClick={() => handleExpand(record)}
                          styles={{ body: { padding: '12px 16px' } }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              marginBottom: '6px',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              <Text
                                strong
                                style={{ fontSize: '13px', color: '#595959' }}
                              >
                                Patient:{' '}
                              </Text>
                              <Text
                                style={{ fontSize: '16px', color: '#000000' }}
                              >
                                {(() => {
                                  const firstName = record?.patientFirstName
                                    ? parseFieldSetup(record.patientFirstName)
                                    : '';
                                  const lastName = record?.patientLastName
                                    ? parseFieldSetup(record.patientLastName)
                                    : '';
                                  const fullName =
                                    `${firstName} ${lastName}`.trim();
                                  return fullName || '-';
                                })()}
                              </Text>
                            </div>
                            <div style={{ fontSize: '13px', color: '#595959' }}>
                              <Text strong>DOB:</Text>{' '}
                              {record?.dob
                                ? dayjs(record?.dob).utc().format('DD MMM YYYY')
                                : '-'}
                            </div>
                            <div style={{ fontSize: '13px', color: '#595959' }}>
                              <Text strong>Agent:</Text>{' '}
                              {record?.agentName || '-'}
                            </div>
                            {record.urgency === 'Urgent' ? (
                              <Tag color="red">Urgent</Tag>
                            ) : (
                              record.urgency && (
                                <Tag color="green">{record.urgency}</Tag>
                              )
                            )}
                            {record?.callType === 'voice' && (
                              <Tag icon={<PhoneOutlined />} color="#4eb5b0">
                                Voice
                              </Tag>
                            )}
                            {record?.callType === 'video' && (
                              <Tag
                                icon={<VideoCameraOutlined />}
                                color="#6f86d6"
                              >
                                Video
                              </Tag>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              marginBottom: '6px',
                              flexWrap: 'wrap',
                              fontSize: '13px',
                              color: '#595959',
                            }}
                          >
                            <div>
                              <Text strong>Multi patient:</Text>{' '}
                              {record?.isMultiPatient === true ? (
                                <Tag
                                  icon={<DeploymentUnitOutlined />}
                                  color="blue"
                                >
                                  Yes
                                </Tag>
                              ) : record?.isMultiPatient === false ? (
                                <Tag icon={<LineOutlined />} color="#8c8c8c">
                                  No
                                </Tag>
                              ) : (
                                '-'
                              )}
                            </div>
                            <div>
                              <Text strong>Call stage:</Text>{' '}
                              {record?.callStep || '-'}
                            </div>
                            <div>
                              <Text strong>Status:</Text>{' '}
                              {translateStatus(record?.currentStatus ?? '') ||
                                '-'}
                            </div>
                            <div>
                              <Text strong>Outcome:</Text>{' '}
                              {record?.outcome || '-'}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#595959',
                              marginBottom: '8px',
                            }}
                          >
                            <Text strong>Start time:</Text>{' '}
                            {record?.startTime
                              ? dayjs(record?.startTime)
                                  .utc()
                                  .format('DD MMM YYYY HH:mm:ss')
                              : '-'}
                          </div>
                        </Card>
                      ))
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#8c8c8c',
                      }}
                    >
                      No active calls
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{ flex: 7 }}>
          <AppointmentTable matchedPairs={matchedPairs} />
        </div>
      </div>
    </>
  );
};

export default PatientTable;
